/**
 * FolderSearchInput · 文件夹名称搜索框（列表工具栏右簇，配额组件旁）
 *
 * 实时接口搜索（接口 2 /web/folder/search，防抖见 useFolderSearch），
 * 结果在输入框下方的下拉面板展示：命中列表（关键词高亮 + 张数/更新时间）、
 * 空态（暂未搜索到结果）、加载态。点击结果跳对应文件夹详情页。
 *
 * 多选模式（manageMode）下不可用：宽度 + 透明度过渡收起（非硬消失），
 * 退出多选恢复。
 */

import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Input, Spin, type InputRef } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { selectManageMode } from '../../../../slices/aiClassify'
import type { FolderSearchItem } from '../../services/folderSearch'
import { useFolderSearch } from './useFolderSearch'
import FolderIconSvg from './FolderIconSvg'
import styles from './FolderSearchInput.module.scss'

/** 关键词输入上限（字符数） */
const KEYWORD_MAX_LENGTH = 30

/** 命中文件夹名里高亮关键词（大小写不敏感，多处命中都标） */
function renderHighlightedName(name: string, keyword: string): React.ReactNode {
  const trimmed = keyword.trim()
  if (!trimmed) return name
  const lowerName = name.toLowerCase()
  const lowerKw = trimmed.toLowerCase()
  const parts: React.ReactNode[] = []
  let cursor = 0
  let hit = lowerName.indexOf(lowerKw)
  while (hit >= 0) {
    if (hit > cursor) parts.push(name.slice(cursor, hit))
    parts.push(
      <em key={hit} className={styles.highlight}>
        {name.slice(hit, hit + trimmed.length)}
      </em>
    )
    cursor = hit + trimmed.length
    hit = lowerName.indexOf(lowerKw, cursor)
  }
  if (cursor < name.length) parts.push(name.slice(cursor))
  return parts
}

/** Unix 秒 → "x 分钟/小时/天前更新"；超过 30 天显示具体日期 */
function formatUpdatedAgo(unixSeconds: number): string {
  if (!unixSeconds || unixSeconds <= 0) return ''
  const diffSec = Math.max(0, Math.floor(Date.now() / 1000) - unixSeconds)
  if (diffSec < 60) return '刚刚更新'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} 分钟前更新`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} 小时前更新`
  if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)} 天前更新`
  const d = new Date(unixSeconds * 1000)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day} 更新`
}

const FolderSearchInput: React.FC = () => {
  const dispatch = useAppDispatch()
  const manageMode = useAppSelector(selectManageMode)
  const navigate = useNavigate()
  const groupID = useAppSelector((state: any) => state.group.groupId)
  const { keyword, onKeywordChange, results, searching, searched } =
    useFolderSearch(String(groupID || ''))
  const [focused, setFocused] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)
  const inputRef = useRef<InputRef>(null)

  const panelVisible = focused && keyword.trim().length > 0

  /**
   * 外点关闭兜底 · 不能只依赖 Input 原生 blur：宫格的框选（useBoxSelect）
   * 在容器 mousedown 时 preventDefault（防文本选择），mousedown 的默认行为
   * 还包括焦点切换——点宫格时输入框根本不会触发 blur。
   * 改为 focused 期间挂 document 级监听（capture 阶段，免疫 stopPropagation）：
   * 点击落在组件（输入框 + 下拉面板）以外 → 关面板并显式失焦。
   */
  useEffect(() => {
    if (!focused) return
    const handleDocMouseDown = (e: MouseEvent): void => {
      const root = wrapRef.current
      if (root && e.target instanceof Node && root.contains(e.target)) return
      setFocused(false)
      if (inputRef.current) inputRef.current.blur()
    }
    document.addEventListener('mousedown', handleDocMouseDown, true)
    return () =>
      document.removeEventListener('mousedown', handleDocMouseDown, true)
  }, [focused])

  /** 行用 onMouseDown（先于 Input blur 触发），blur 关面板不会吃掉点击 */
  const handleResultClick = (item: FolderSearchItem): void => {
    navigate(`/ai-classify/folder/${item.folderID}`)
  }

  return (
    <span
      ref={wrapRef}
      className={classNames(styles.wrap, {
        [styles.wrapHidden]: manageMode
      })}
      aria-hidden={manageMode}
    >
      <Input
        ref={inputRef}
        className={styles.input}
        value={keyword}
        allowClear
        maxLength={KEYWORD_MAX_LENGTH}
        prefix={<SearchOutlined className={styles.searchIcon} />}
        placeholder="搜索文件夹名称"
        onChange={(e) => onKeywordChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') (e.target as HTMLInputElement).blur()
        }}
      />

      {panelVisible && (
        <div
          className={styles.panel}
          role="listbox"
          aria-label="文件夹搜索结果"
          // 面板内任意位置（含头部计数 / 空态 / 空白 padding）按下都阻止
          // 焦点切换的默认行为 → 输入框不 blur、面板不关；行点击在冒泡阶段照常处理
          onMouseDown={(e) => e.preventDefault()}
        >
          {searching && results.length === 0 ? (
            <div className={styles.panelState}>
              <Spin size="small" />
            </div>
          ) : searched && results.length === 0 ? (
            <div className={styles.panelEmpty}>
              <span className={styles.emptyIcon}>
                <FolderIconSvg variant="manual" size={48} />
                <span className={styles.emptyMagnifier}>
                  <SearchOutlined />
                </span>
              </span>
              <div className={styles.emptyTitle}>暂未搜索到结果</div>
              <div className={styles.emptyDesc}>请调整关键词后重试</div>
            </div>
          ) : (
            <>
              <div className={styles.panelHeader}>
                {results.length} 个文件夹
              </div>
              {results.map((item) => (
                <div
                  key={item.folderID}
                  className={styles.resultRow}
                  role="option"
                  aria-selected={false}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleResultClick(item)
                  }}
                >
                  <span className={styles.resultIcon}>
                    <FolderIconSvg
                      variant="manual"
                      size={36}
                      isAi={item.isIntelligenceFolder}
                      hasChildren={item.folderCount > 0}
                    />
                  </span>
                  <span className={styles.resultBody}>
                    <span className={styles.resultName} title={item.folderName}>
                      {renderHighlightedName(item.folderName, keyword)}
                    </span>
                    <span className={styles.resultMeta}>
                      {item.photoCount.toLocaleString('en-US')} 张
                      {formatUpdatedAgo(item.folderUpdateTime) &&
                        ` · ${formatUpdatedAgo(item.folderUpdateTime)}`}
                    </span>
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </span>
  )
}

export default FolderSearchInput
